import React, { useCallback, useEffect, useState } from "react";
import { logix2Dock } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — LOGIX-2™ Voice Settings Panel (Phase 20)
 *
 * Per-user voice preferences. Server-stored.
 */
export default function Logix2VoiceSettings({ onChange, compact = false } = {}) {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [voices, setVoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ data: s }, { data: st }] = await Promise.all([
          logix2Dock.voiceSettings(),
          logix2Dock.voiceStatus(),
        ]);
        if (!mounted) return;
        setSettings(s?.settings || {});
        setStatus(st || null);
      } catch (e) {
        if (mounted) setErr(e?.message || "failed_to_load");
      }
    })();
    // Browser voices (may load asynchronously)
    try {
      const synth = window.speechSynthesis;
      const loadVoices = () => {
        const list = (synth.getVoices() || []).map((v) => ({ name: v.name, lang: v.lang }));
        setVoices(list);
      };
      loadVoices();
      if (synth && "onvoiceschanged" in synth) {
        synth.addEventListener("voiceschanged", loadVoices);
        return () => { mounted = false; synth.removeEventListener("voiceschanged", loadVoices); };
      }
    } catch { /* ignore */ }
    return () => { mounted = false; };
  }, []);

  const patch = useCallback(async (delta) => {
    setBusy(true); setErr("");
    try {
      const { data } = await logix2Dock.voiceSettingsPatch(delta);
      setSettings(data?.settings || {});
      setSavedAt(new Date().toLocaleTimeString());
      onChange && onChange(data?.settings || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "save_failed");
    } finally {
      setBusy(false);
    }
  }, [onChange]);

  if (!settings) return <div className="l2-vs__loading" data-testid="logix2-voice-settings-loading">Loading voice settings…</div>;

  return (
    <div className={`l2-vs ${compact ? "is-compact" : ""}`} data-testid="logix2-voice-settings">
      {err && <div className="l2-vs__err" role="alert">{err}</div>}
      <div className="l2-vs__grid">
        <label className="l2-vs__row">
          <span>Voice Enabled</span>
          <input type="checkbox" checked={!!settings.enabled} onChange={(e) => patch({ enabled: e.target.checked })} disabled={busy} data-testid="logix2-vs-enabled" />
        </label>
        <label className="l2-vs__row">
          <span>Auto-read responses</span>
          <input type="checkbox" checked={!!settings.auto_read} onChange={(e) => patch({ auto_read: e.target.checked })} disabled={busy} data-testid="logix2-vs-auto" />
        </label>
        <label className="l2-vs__row">
          <span>Read short summary only</span>
          <input type="checkbox" checked={!!settings.summary_only} onChange={(e) => patch({ summary_only: e.target.checked })} disabled={busy} data-testid="logix2-vs-summary" />
        </label>
        <label className="l2-vs__row">
          <span>Read disclaimers</span>
          <input type="checkbox" checked={!!settings.read_disclaimers} onChange={(e) => patch({ read_disclaimers: e.target.checked })} disabled={busy} data-testid="logix2-vs-discl" />
        </label>
        <label className="l2-vs__row">
          <span>Reduced audio</span>
          <input type="checkbox" checked={!!settings.reduced_audio} onChange={(e) => patch({ reduced_audio: e.target.checked })} disabled={busy} data-testid="logix2-vs-reduced" />
        </label>
        <label className="l2-vs__row">
          <span>Provider</span>
          <select value={settings.provider || "browser"} onChange={(e) => patch({ provider: e.target.value })} disabled={busy} data-testid="logix2-vs-provider">
            <option value="browser">Browser (free)</option>
            <option value="elevenlabs" disabled={!status?.tts?.elevenlabs_configured}>
              ElevenLabs {status?.tts?.elevenlabs_configured ? "" : "(not configured)"}
            </option>
          </select>
        </label>
        {settings.provider === "browser" && (
          <label className="l2-vs__row">
            <span>Browser voice</span>
            <select value={settings.voice_name || "default"} onChange={(e) => patch({ voice_name: e.target.value })} disabled={busy} data-testid="logix2-vs-voicename">
              <option value="default">Default</option>
              {voices.map((v) => <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>)}
            </select>
          </label>
        )}
        <label className="l2-vs__row">
          <span>Speed ({Number(settings.rate || 1).toFixed(2)})</span>
          <input type="range" min="0.5" max="2.0" step="0.05" value={settings.rate || 1.0} onChange={(e) => patch({ rate: parseFloat(e.target.value) })} disabled={busy} data-testid="logix2-vs-rate" />
        </label>
        <label className="l2-vs__row">
          <span>Pitch ({Number(settings.pitch || 1).toFixed(2)})</span>
          <input type="range" min="0.5" max="2.0" step="0.05" value={settings.pitch || 1.0} onChange={(e) => patch({ pitch: parseFloat(e.target.value) })} disabled={busy} data-testid="logix2-vs-pitch" />
        </label>
      </div>
      <p className="l2-vs__foot">
        Provider status: <code>{status?.tts?.elevenlabs_configured ? "ElevenLabs ON" : "Browser TTS ON"}</code> · STT: <code>{status?.stt?.openai_whisper_configured ? "Whisper ON" : "Browser STT ON"}</code>
        {savedAt && <span> · Saved {savedAt}</span>}
      </p>
    </div>
  );
}
